import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'

/**
 * A denormalized dog header, byte-identical to a `hunts/{id}.dogs` entry and to
 * what mobile writes (CLUB-883). `dogColor` is a packed ARGB int; `dogIcon` is
 * the icon id; `devices` are the dog's collar ids, carried through so a
 * pre-registered dog keeps its collar when the hunt starts.
 */
export interface PlanDogHeaderWire {
  id: string
  name: string
  ownerName: string
  dogColor: number
  dogIcon: string
  devices: string[]
  visibility?: string | null
}

export interface RegisterPlanDogsVars {
  add: PlanDogHeaderWire[]
  remove: string[]
}

/**
 * Registers (and unregisters) the signed-in user's dogs on a PLANNED hunt,
 * through the `registerplandogs` callable (europe-west1) — the same function
 * the mobile app uses.
 *
 * This reaches a Firebase callable **directly**, a deliberate exception to the
 * web-data-access rule (which bans `firebase/functions`), identical in kind to
 * `useRespondToHuntInvite` and `useJoinHuntByCode`: the plan document is
 * leader/server-controlled, so a participant cannot write it from the client,
 * and the callable already owns the whole gate (participant/leader, per-dog
 * ownership, refuse-if-started). Routing web through its own .NET write would
 * fork that enforcement and leave two copies to keep in step.
 *
 * The callable **throws** on every refusal (it reports failure as an HttpsError,
 * not a resolved error envelope), so a rejection flows straight to the
 * mutation's `onError`. The backend's own wording is logged for diagnostics but
 * never surfaced — it is server phrasing, sometimes a raw request dump — and the
 * caller shows its own message instead.
 */
export function useRegisterPlanDogs(
  groupId: string | undefined,
  huntPlanId: string | undefined,
) {
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ add, remove }: RegisterPlanDogsVars): Promise<void> => {
      if (!huntPlanId) throw new Error('Missing hunt plan id for dog registration')
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<
        { huntPlanId: string; add: PlanDogHeaderWire[]; remove: string[] },
        { status?: string; message?: string }
      >(fns, 'registerplandogs')
      try {
        await call({ huntPlanId, add, remove })
      } catch (e) {
        console.error('registerplandogs rejected the request', e)
        throw new Error('The dogs could not be saved.')
      }
    },
    onSuccess: () => {
      // Refresh the open plan detail so the Dogs section reflects the change.
      void qc.invalidateQueries({
        queryKey: ['clubhouse', 'groups', groupId, 'hunts', huntPlanId, 'detail', uid],
      })
    },
  })
}
